import React, { type HTMLAttributes, type KeyboardEvent, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import useOpaqueId from '@jwp/ott-hooks-react/src/useOpaqueId';
import { testId } from '@jwp/ott-common/src/utils/common';

import type { Props as TabProps } from '../Tab/Tab';

import styles from './Tabs.module.scss';

type MakeArray<T> = T | T[];
type Children = MakeArray<React.ReactElement<TabProps> | null>;

type Props = {
  children: Children;
  onTabSelect: (index: number) => void;
  selectedTab: number;
  className?: string;
  keyboardNavigation?: boolean;
} & HTMLAttributes<HTMLDivElement>;

type TabButtonProps = {
  index: number;
  title: string;
  onTabSelect: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  selectedTab: boolean;
  prefixId: string;
};

const TabButton = ({ selectedTab, onTabSelect, onKeyDown, index, title, prefixId }: TabButtonProps) => {
  return (
    <button
      key={index}
      role="tab"
      aria-controls="tab-content"
      aria-selected={selectedTab}
      aria-label={testId(`tab-${index}`)}
      tabIndex={selectedTab ? 0 : -1}
      id={`${prefixId}-${index}`}
      onClick={onTabSelect}
      onKeyDown={onKeyDown}
      className={classNames(styles.tabButton, { [styles.active]: selectedTab })}
      data-title={title}
    >
      {title}
    </button>
  );
};

const Tabs: React.FC<Props> = ({ children, selectedTab, onTabSelect, className, keyboardNavigation = true, ...rest }) => {
  const tabs = React.Children.toArray(children) as React.ReactElement<TabProps>[];
  const tabListRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const tabPrefixId = useOpaqueId('tab-button');

  useEffect(() => {
    const button = tabListRef.current?.querySelectorAll('button')[selectedTab];

    if (!button) return;

    // this assumes that the parent is rendered without extra padded containers
    const parentOffset = tabListRef.current?.getBoundingClientRect()?.left || 0;
    const buttonRect = button.getBoundingClientRect();
    const scale = button.currentCSSZoom || 1; // handle zoom if necessary

    setIndicatorStyle({ left: (buttonRect.left - parentOffset) / scale, width: buttonRect.width / scale });
  }, [selectedTab, children]);

  const moveFocusToTab = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    event.preventDefault();
    (tabListRef.current?.querySelectorAll('[role="tab"]')[index] as HTMLButtonElement)?.focus();
    onTabSelect(index);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    switch (event.key) {
      case 'ArrowLeft':
        return moveFocusToTab(event, currentIndex > 0 ? currentIndex - 1 : tabs.length - 1);
      case 'ArrowRight':
        return moveFocusToTab(event, currentIndex < tabs.length - 1 ? currentIndex + 1 : 0);
      case 'Home':
        return moveFocusToTab(event, 0);
      case 'End':
        return moveFocusToTab(event, tabs.length - 1);
      default:
        return;
    }
  };

  return (
    <>
      <div className={styles.tabsHeaderContainer}>
        <div className={classNames(styles.tabHeader, className)} role="tablist" ref={tabListRef} {...rest}>
          {tabs.map((item, index) => (
            <TabButton
              prefixId={tabPrefixId}
              selectedTab={selectedTab === index}
              key={item.props.title}
              index={index}
              title={item.props.title}
              onTabSelect={() => onTabSelect(index)}
              onKeyDown={keyboardNavigation ? (event) => handleKeyDown(event, index) : undefined}
            />
          ))}
          <div className={styles.indicator} aria-hidden="true" style={indicatorStyle} role="presentation" />
        </div>
      </div>
      <div id="tab-content" className={styles.tabsContent}>
        {tabs[selectedTab]}
      </div>
    </>
  );
};

export default Tabs;
